import { Alert, Col, Row, Space, Switch } from 'antd';
import {
  FloorplanGeoPersonalizationPanel,
  PersonalizationSteps,
  usePersonalizationSteps,
} from '../Personalization.Details';
import { PersonalizationDetails } from './PersonalizationDetails';
import { FinalizationAlert } from './FinalizationAlert';
import { FinalizationResidents } from './FinalizationResidents';
import { FinalizeButton } from './FinalizeButton';
import { PaymentsPanel } from './Payments';
import { useProjectContext } from '@/views';
import { useGetProjectHomeInfoQuery } from '@/redux';
import { PersonalizationSummaryTotalBalance } from './PersonalizationSummaryTotalBalance';
import {
  PriceColumnsCheckboxGroup,
  usePriceColumnsCheckboxGroup,
} from './PriceColumnsCheckboxGroup';
import { useTranslation } from 'react-i18next';
import { isArray } from 'lodash';
import { DownloadPdfButton } from './DownloadPdfButton';
import { useBool } from '@/utils';

interface Props {
  homeId: string;
  communityId: string;
  projectId: string;
  isPublic?: boolean;
}

export function PersonalizationSummaryContent(props: Props) {
  const { homeId, projectId, communityId, isPublic } = props;

  const { t } = useTranslation();

  const { isActive } = useProjectContext();

  var { data: projectHome } = useGetProjectHomeInfoQuery({
    id: projectId,
    homeId,
    allowAnonymous: isPublic,
  });

  const steps = usePersonalizationSteps({ projectId, projectHome, isPublic });
  const priceColumnsCheckboxGroup = usePriceColumnsCheckboxGroup();
  const { showBaseCost, showResidentPrice, showOwnerPrice } =
    priceColumnsCheckboxGroup;
  const selectedStepId = isArray(steps.selected)
    ? steps.selected[0]
    : steps.selected;

  const selectedStep = steps.steps.find((x) => x.id === selectedStepId);

  const allPersonalizationsSelected = !selectedStep;

  const [showFlooroplanImage, , toggleShowFlooroplanImage] = useBool(true);

  if (!projectHome?.menuId) {
    return (
      <Alert
        type="info"
        description={t('personalization.floorplan.geo.noMenu')}
      />
    );
  }

  return (
    <Space direction="vertical" size="middle" className="w-100">
      {!isPublic && (
        <Row justify="space-between">
          <Space direction="horizontal">
            <PersonalizationSteps {...steps} />
            <DownloadPdfButton
              communityId={communityId}
              projectId={projectId}
              homeId={homeId}
              showBaseCost={showBaseCost}
              showResidentPrice={showResidentPrice}
              selectedStepId={selectedStep?.id}
            />
          </Space>
        </Row>
      )}

      {!isPublic && selectedStep?.id && isActive && (
        <FinalizeButton
          projectId={projectId}
          homeId={homeId}
          selectedStepId={selectedStep?.id}
        />
      )}
      <Row justify={isPublic ? 'end' : 'start'} className="w-100">
        <Space className="mb-0">
          <Switch
            checked={showFlooroplanImage}
            onChange={toggleShowFlooroplanImage}
          />
          {t('personalization.summary.showFloorplanImage')}
        </Space>
      </Row>

      {!isPublic && allPersonalizationsSelected && (
        <Col span={12}>
          <FinalizationAlert homeId={homeId} projectId={projectId} />
        </Col>
      )}
      {showFlooroplanImage && (
        <FloorplanGeoPersonalizationPanel
          homeId={homeId}
          projectId={projectId}
          selectedStepIds={selectedStep ? [selectedStep.id] : undefined}
          readonly
          isPublic={isPublic}
        />
      )}
      {!isPublic && (
        <PriceColumnsCheckboxGroup {...priceColumnsCheckboxGroup} />
      )}

      <PersonalizationDetails
        homeId={homeId}
        projectId={projectId}
        stepId={selectedStep?.id}
        showBaseCost={showBaseCost}
        showResidentPrice={showResidentPrice}
        showOwnerPrice={showOwnerPrice}
        className="mb-3"
        isPublic={isPublic}
        communityId={communityId}
      />
      {!isPublic && (
        <PaymentsPanel
          className="w-100"
          homeId={homeId}
          projectId={projectId}
          stepId={selectedStep?.id}
        />
      )}

      {!isPublic && allPersonalizationsSelected && (
        <PersonalizationSummaryTotalBalance
          homeId={homeId}
          projectId={projectId}
          showBaseCost={showBaseCost}
          showResidentPrice={showResidentPrice}
        />
      )}
      {!isPublic && projectHome?.isActive && (
        <FinalizationResidents
          projectId={projectId}
          homeId={homeId}
          selectedStepId={selectedStep?.id}
        />
      )}
    </Space>
  );
}
